/** Only genuine Telegram photos should enter the download/upload pipeline. */
export function hasAvatarPhoto(photo) {
  return !!photo && !/Empty$/.test(photo._ || '') && /^-?[1-9]\d*$/.test(String(photo.photo_id || ''));
}

/** Fetch and decode actual image bytes. Optional avatars must never interrupt reporting. */
export async function readAvatarBlob(url) {
  if (typeof url !== 'string' || !/^(?:blob:|https?:\/\/|data:image\/)/i.test(url.trim())) return null;
  try {
    const response = await fetch(url.trim());
    if (!response.ok) return null;
    let blob = await response.blob();
    if (!blob.size || (blob.type && !/^(?:image\/(?:png|jpe?g|gif|webp|avif|bmp)|application\/octet-stream)$/i.test(blob.type))) return null;

    // Blob MIME can be absent or incorrect; derive the upload MIME from the bytes.
    const bytes = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    const startsWith = (...signature) => signature.every((value, index) => bytes[index] === value);
    let contentType = '';
    if (startsWith(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) contentType = 'image/png';
    else if (startsWith(0xff, 0xd8, 0xff)) contentType = 'image/jpeg';
    else if (startsWith(0x47, 0x49, 0x46, 0x38) && (bytes[4] === 0x37 || bytes[4] === 0x39) && bytes[5] === 0x61) contentType = 'image/gif';
    else if (startsWith(0x52, 0x49, 0x46, 0x46) && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) contentType = 'image/webp';
    else if (startsWith(0x42, 0x4d)) contentType = 'image/bmp';
    else if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70 && bytes[8] === 0x61 && bytes[9] === 0x76 && bytes[10] === 0x69 && (bytes[11] === 0x66 || bytes[11] === 0x73)) contentType = 'image/avif';
    if (!contentType) return null;
    blob = blob.slice(0, blob.size, contentType);

    const bitmap = await createImageBitmap(blob);
    try {
      if (!bitmap.width || !bitmap.height) return null;
    } finally {
      bitmap.close();
    }
    return blob;
  } catch {
    return null;
  }
}

/** @returns {Promise<string>} */
export async function avatarBlobToBase64(blob) {
  if (!blob?.size || !/^image\//i.test(blob.type)) return '';
  try {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.onerror = reject;
      reader.onabort = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return '';
  }
}

export async function avatarUrlToBase64(url) {
  const blob = await readAvatarBlob(url);
  return blob ? avatarBlobToBase64(blob) : '';
}
